//@flow
import {
    fpPermanentJourneyPlanFetch as FpPjPlanFetchApi,
    fpSaleseatPlanFetch as FpSalesbeatFetchApi,
    fpMoveVisit as FpMoveVisitApi,
    pjpPlanFreeze as PjpPlanFreezeApi,
    pjpSalesCategorySearch,
    serializedSchedulingConfigFetchApi
} from 'common/middleware/api';
import type { Dispatch } from 'redux';

import { injectError } from 'common/redux/actions/error';
import { ApiError } from 'common/modules/api';
import { UserVisitTypeEnum } from 'common/models/flow/BeatVisit';

type Params = {
    clientId: string,
    pjpId: string
};

const handleApiError = (error: any, dispatch: Function): void => {
    if (error instanceof ApiError) {
        // Show error on UI only if it is API Exception
        dispatch(injectError(error));
        return;
    }
    throw error;
};

const _calculateTotalVolumeAndMultiTripVolume = beats => {
    // beats will be mutated
    const totalVolume = beats.reduce(
        (accumulatedVolume, { summary, visits }) => {
            const havingMultiTrip = visits.find(
                ({ userVisitType }) =>
                    userVisitType === UserVisitTypeEnum.RELOAD
            );
            if (havingMultiTrip) {
                let totalTripVolume = 0;
                const multiTripVolume = [];
                visits.forEach(
                    ({ outletVisitId, userVisitType, volume }, index) => {
                        if (
                            userVisitType === UserVisitTypeEnum.RELOAD ||
                            index + 1 === visits.length
                        ) {
                            multiTripVolume.push(totalTripVolume);
                            totalTripVolume = 0;
                            if (index + 1 === visits.length) {
                                summary.multiTripVolume = multiTripVolume;
                            }
                        } else if (outletVisitId) {
                            totalTripVolume += volume;
                        }
                    }
                );
            }
            return accumulatedVolume + (summary.totalVolume || 0);
        },
        0
    );
    return { totalVolume, beats };
};

export default class FpPlanViewActions {
    static FETCH_FP_PLAN_START = 'FETCH_FP_PLAN_START';
    static FETCH_FP_PLAN_SUCCESS = 'FETCH_FP_PLAN_SUCCESS';
    static FETCH_FP_PLAN_FAILURE = 'FETCH_PL_PLAN_FAILURE';
    static FETCH_FP_PLAN_TOURS_SUCCESS = 'FETCH_FP_PLAN_TOURS_SUCCESS';
    static FETCH_FP_PLAN_TOURS_START = 'FETCH_FP_PLAN_TOURS_START';
    static FETCH_FP_SALESBEAT_PLAN_START = 'FETCH_FP_SALESBEAT_PLAN_START';
    static FETCH_FP_SALESBEAT_PLAN_SUCCESS = 'FETCH_FP_SALESBEAT_PLAN_SUCCESS';
    static FETCH_FP_SALESBEAT_PLAN_FAILURE = 'FETCH_FP_SALESBEAT_PLAN_FAILURE';
    static FREEZE_PLAN_START = 'FREEZE_PLAN_START';
    static FREEZE_PLAN_SUCCESS = 'FREEZE_PLAN_SUCCESS';
    static FREEZE_PLAN_FAILURE = 'FREEZE_PLAN_FAILURE';
    static CLEAR_ALL_FP_DATA = 'CLEAR_ALL_FP_DATA';
    static OUTELT_SALES_CATEGORY_FETCH_IN_PROGRESS =
        'OUTELT_SALES_CATEGORY_FETCH_IN_PROGRESS';
    static OUTELT_SALES_CATEGORY_FETCH_SUCCESS =
        'OUTELT_SALES_CATEGORY_FETCH_SUCCESS';
    static OUTELT_SALES_CATEGORY_FETCH_ERROR =
        'OUTELT_SALES_CATEGORY_FETCH_ERROR';
    static EDIT_VISIT_SUCCESS = 'EDIT_VISIT_SUCCESS';

    static getPlanEdits = params => {
        return {
            type: FpPlanViewActions.GET_PLAN_EDITS,
            params
        };
    };

    static fetchFpPlanStart = (params: Params) => {
        return {
            type: FpPlanViewActions.FETCH_FP_PLAN_START,
            params
        };
    };

    static fetchFpPlanSuccess = (plan, params: Params) => {
        return {
            type: FpPlanViewActions.FETCH_FP_PLAN_SUCCESS,
            params,
            plan
        };
    };

    static fetchFpPlanFailure = (error, params) => {
        return {
            type: FpPlanViewActions.FETCH_FP_PLAN_FAILURE,
            params,
            error
        };
    };

    static fetchFpSalesbeatPlanStart = (params: Params) => {
        return {
            type: FpPlanViewActions.FETCH_FP_SALESBEAT_PLAN_START,
            params
        };
    };

    static fetchFpSalesbeatPlanSuccess = (salesbeatPlan, params: Params) => {
        return {
            type: FpPlanViewActions.FETCH_FP_SALESBEAT_PLAN_SUCCESS,
            params,
            salesbeatPlan
        };
    };

    static fetchFpSalesbeatPlanFailure = (error, params) => {
        return {
            type: FpPlanViewActions.FETCH_FP_SALESBEAT_PLAN_FAILURE,
            params,
            error
        };
    };

    static editVisitSuccess = (params, salesbeatPlan) => {
        return {
            type: FpPlanViewActions.EDIT_VISIT_SUCCESS,
            params,
            salesbeatPlan
        };
    };

    static clearFpData = () => {
        return {
            type: FpPlanViewActions.CLEAR_ALL_FP_DATA
        };
    };

    static setIsFetchingSalesCategoriesInProgress = value => {
        return {
            type: FpPlanViewActions.OUTELT_SALES_CATEGORY_FETCH_IN_PROGRESS,
            value
        };
    };

    static setIsFetchingSalesCategoriesError = error => {
        return {
            type: FpPlanViewActions.OUTELT_SALES_CATEGORY_FETCH_ERROR,
            error
        };
    };

    static injectOutletSalesCategories = salesCategories => {
        return {
            type: FpPlanViewActions.OUTELT_SALES_CATEGORY_FETCH_SUCCESS,
            salesCategories
        };
    };

    static freezePlan = (clientId, pjpId) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const params = {
                    clientId,
                    pjpId
                };
                dispatch(FpPlanViewActions.fetchFpPlanStart(params));
                PjpPlanFreezeApi.post(params)
                    .then(frozenPlan => {
                        dispatch(
                            FpPlanViewActions.fetchFpPlanSuccess(
                                frozenPlan,
                                params
                            )
                        );
                        resolve(frozenPlan);
                    })
                    .catch(error => {
                        reject(error);
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                        } else {
                            throw error;
                        }
                    });
            });
        };
    };

    static fetchFpPjPlanCall = (params: {
        clientId: string,
        pjpId: string,
        include?: 'VISITS'
    }) => {
        return new Promise((resolve, reject) => {
            FpPjPlanFetchApi.fetch(params)
                .then(resolve)
                .catch(error => {
                    reject(error);
                });
        });
    };

    static fetchFpPjPlan = (clientId: string, pjpId: string) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const params = {
                    clientId,
                    pjpId,
                    include: 'VISITS'
                };
                dispatch(FpPlanViewActions.fetchFpPlanStart(params));
                FpPlanViewActions.fetchFpPjPlanCall(params)
                    .then(async plan => {
                        if (plan.salesbeatPlan) {
                            const { beats } = plan.salesbeatPlan;
                            const sortedVisibleBeats = beats.sort((a, b) => {
                                return a.name.localeCompare(b.name, 'en', {
                                    numeric: true
                                });
                            });
                            plan.salesbeatPlan.beats = sortedVisibleBeats;
                            const {
                                totalVolume,
                                beats: nextBeats
                            } = _calculateTotalVolumeAndMultiTripVolume(
                                plan.salesbeatPlan.beats
                            );
                            plan.salesbeatPlan.summary.totalVolume = totalVolume;
                            plan.salesbeatPlan.beats = nextBeats;
                        }
                        if (!plan.configuration.serializedSchedulingConfig) {
                            const {
                                serializedSchedulingConfig
                            } = await serializedSchedulingConfigFetchApi.fetch({
                                clientId,
                                configId:
                                    plan.configuration.schedulingConfigId
                                        .configId
                            });
                            plan.configuration.serializedSchedulingConfig = serializedSchedulingConfig;
                        }
                        dispatch(
                            FpPlanViewActions.fetchFpPlanSuccess(plan, params)
                        );
                        resolve(plan);
                    })
                    .catch(error => {
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                        } else {
                            throw error;
                        }
                        reject(error);
                    });
            });
        };
    };

    static fetchPjSalesBeatPlan = (clientId: string, pjpId: string) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const params = {
                    clientId,
                    pjpId
                };
                dispatch(FpPlanViewActions.fetchFpSalesbeatPlanStart(params));
                FpSalesbeatFetchApi.fetch(params)
                    .then(salesbeatPlan => {
                        if (salesbeatPlan) {
                            const { beats } = salesbeatPlan;
                            const sortedVisibleBeats = beats.sort((a, b) => {
                                return a.name.localeCompare(b.name, 'en', {
                                    numeric: true
                                });
                            });
                            salesbeatPlan.beats = sortedVisibleBeats;
                            dispatch(
                                FpPlanViewActions.fetchFpSalesbeatPlanSuccess(
                                    salesbeatPlan,
                                    params
                                )
                            );
                        } else {
                            dispatch(
                                FpPlanViewActions.fetchFpSalesbeatPlanSuccess(
                                    null,
                                    params
                                )
                            );
                        }
                        resolve(salesbeatPlan);
                    })
                    .catch(error => {
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                        } else {
                            throw error;
                        }
                        reject(error);
                    });
            });
        };
    };

    static editVisit = ({ clientId, pjpId, planId }, edits, dryRun = false) => {
        return (dispatch: Dispatch<*>) => {
            const params = {
                clientId,
                pjpId,
                planId
            };
            const body = {
                dryRun,
                edits
            };
            return new Promise((resolve, reject) => {
                FpMoveVisitApi.post(params, body)
                    .then(salesbeatPlan => {
                        const { beats } = salesbeatPlan;
                        const sortedVisibleBeats = beats.sort((a, b) => {
                            return a.name.localeCompare(b.name, 'en', {
                                numeric: true
                            });
                        });
                        salesbeatPlan.beats = sortedVisibleBeats;
                        const {
                            totalVolume,
                            beats: nextBeats
                        } = _calculateTotalVolumeAndMultiTripVolume(beats);
                        salesbeatPlan.summary.totalVolume = totalVolume;
                        salesbeatPlan.beats = nextBeats;
                        resolve(salesbeatPlan);
                        dispatch(
                            FpPlanViewActions.editVisitSuccess(
                                params,
                                salesbeatPlan
                            )
                        );
                    })
                    .catch(error => {
                        handleApiError(error, dispatch);
                        reject(error);
                    });
            });
        };
    };

    static fetchOutletSalesCategories = (clientId: string, pjpId: string) => {
        return (dispatch: Dispatch<*>) => {
            dispatch(
                FpPlanViewActions.setIsFetchingSalesCategoriesInProgress(true)
            );
            const params = { clientId, pjpId };
            return pjpSalesCategorySearch
                .fetch(params)
                .then(response => {
                    dispatch(
                        FpPlanViewActions.injectOutletSalesCategories(
                            response.salesCategories
                        )
                    );
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        dispatch(
                            FpPlanViewActions.setIsFetchingSalesCategoriesError(
                                error
                            )
                        );
                        dispatch(injectError(error));
                    } else {
                        throw error;
                    }
                });
        };
    };

    static clearFpPlanViewData = () => {
        return (dispatch: Dispatch<*>) => {
            dispatch(FpPlanViewActions.clearFpData());
        };
    };
}
